import React, { useEffect, useMemo, useRef, useState } from "react";
import { CaretRight, ShieldCheck } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { getStoredSessionToken } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { isUnlocked as isVaultUnlocked, lock as lockVault, hasPin } from "../../lib/vaultLock";
import { confirmDelete } from "../../lib/confirmDelete";
import BrandMark from "./BrandMark";
import AccountButton from "../AccountButton";
import { VaultIllustration } from "./illustrations";
import VaultLockScreen from "./VaultLockScreen";

import {
  CATEGORIES,
  CAT_TO_LIST,
  MAX_FAMILY,
  VAULT_SURFACE_STYLE,
} from "./vault/vaultSchema";
import { readVault, writeVault, mergeVaults } from "./vault/vaultStorage";
import CatIcon from "./vault/CatIcon";
import SyncStatusPill from "./vault/SyncStatusPill";
import CategoryView from "./vault/CategoryView";
import PlaceholderCategory from "./vault/PlaceholderCategory";

/**
 * Travel Vault v1.3 — orchestrator only.
 *
 * Heavy lifting (categories, forms, cards, schema) lives under
 * `./vault/*` so this file stays focused on:
 *   • local-state + cloud-sync wiring (GET/PUT /api/vault)
 *   • PIN gate
 *   • category list ↔ detail-screen routing
 *   • the Vault home grid
 */
export default function TravelVaultTab({ openPaywall, onBack, pendingAction, consumeAction }) {
  const { user } = useAuth();
  const isSignedIn = !!user;
  const [vault, setVault] = useState(() => readVault());
  const [activeCat, setActiveCat] = useState(null);
  const [adding, setAdding] = useState(false);
  const [syncStatus, setSyncStatus] = useState(isSignedIn ? "syncing" : "idle");
  const [unlocked, setUnlocked] = useState(() => isVaultUnlocked());

  // Re-evaluate lock state each time the user navigates back to the Vault tab.
  // If a PIN exists but the session was previously locked, prompt again.
  useEffect(() => {
    setUnlocked(isVaultUnlocked());
  }, []);

  // Auto re-lock the Vault when the user leaves the tab (unmount). The lock
  // screen will appear the next time they tap the Vault icon.
  useEffect(() => {
    return () => {
      if (hasPin()) lockVault();
    };
  }, []);

  useEffect(() => writeVault(vault), [vault]);

  // Auto-open a category when the user arrives via a Home pill (e.g.
  // `navigate("vault:photo_id")` → pendingAction = "photo_id").
  const consumedRef = useRef(false);
  useEffect(() => {
    if (!pendingAction || consumedRef.current) return;
    const isValid = CATEGORIES.some((c) => c.id === pendingAction);
    if (isValid) {
      consumedRef.current = true;
      setActiveCat(pendingAction);
      consumeAction?.();
    }
  }, [pendingAction, consumeAction]);

  // Pull from cloud on sign-in. Only fire when BOTH a user object AND a
  // stored session token exist — otherwise the request is guaranteed to 401.
  const fetchedRef = useRef(false);
  useEffect(() => {
    if (!isSignedIn || !getStoredSessionToken()) {
      setSyncStatus("idle");
      fetchedRef.current = false;
      return;
    }
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    setSyncStatus("syncing");
    api
      .get("/vault")
      .then((r) => {
        const cloud = r.data?.vault || {};
        setVault((local) => mergeVaults(local, cloud));
        setSyncStatus("synced");
      })
      .catch(() => setSyncStatus("offline"));
  }, [isSignedIn]);

  // Debounced push.
  const pushTimerRef = useRef(null);
  useEffect(() => {
    if (!isSignedIn || !getStoredSessionToken()) return;
    if (syncStatus === "syncing") return;
    if (pushTimerRef.current) clearTimeout(pushTimerRef.current);
    pushTimerRef.current = setTimeout(() => {
      setSyncStatus("syncing");
      api
        .put("/vault", { vault })
        .then(() => setSyncStatus("synced"))
        .catch(() => setSyncStatus("offline"));
    }, 600);
    return () => {
      if (pushTimerRef.current) clearTimeout(pushTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vault, isSignedIn]);

  const counts = useMemo(
    () =>
      Object.fromEntries(
        CATEGORIES.map((c) => [c.id, (vault[CAT_TO_LIST[c.id]] || []).length])
      ),
    [vault]
  );

  function addEntry(catId, entry) {
    const listKey = CAT_TO_LIST[catId];
    if (!listKey) return;
    if (catId === "family" && vault.family.length >= MAX_FAMILY) {
      toast.error(`Maximum ${MAX_FAMILY} family members`);
      return;
    }
    const id = `e_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
    const stamped = { id, ...entry, created_at: Date.now() };
    setVault((v) => ({ ...v, [listKey]: [stamped, ...v[listKey]] }));
    toast.success("Saved to your Travel Vault");
  }

  async function removeEntry(catId, id) {
    const listKey = CAT_TO_LIST[catId];
    if (!listKey) return;
    if (!(await confirmDelete())) return;
    setVault((v) => ({ ...v, [listKey]: v[listKey].filter((e) => e.id !== id) }));
    toast.message("Removed");
  }

  // PIN gate — render AFTER all hooks/handlers are declared so hook order is stable.
  if (!unlocked) {
    return (
      <VaultLockScreen
        onUnlocked={() => setUnlocked(true)}
        onBack={onBack}
      />
    );
  }

  if (activeCat) {
    const cat = CATEGORIES.find((c) => c.id === activeCat);
    if (!cat?.live) {
      return <PlaceholderCategory cat={cat} onBack={() => setActiveCat(null)} />;
    }
    const entries = vault[CAT_TO_LIST[activeCat]] || [];
    return (
      <CategoryView
        cat={cat}
        entries={entries}
        familyMembers={vault.family}
        onBack={() => {
          setActiveCat(null);
          setAdding(false);
        }}
        onAdd={() => setAdding(true)}
        onRemove={(id) => removeEntry(cat.id, id)}
        adding={adding}
        onSave={(entry) => {
          addEntry(cat.id, entry);
          setAdding(false);
        }}
        onCancelAdd={() => setAdding(false)}
      />
    );
  }

  return (
    <div
      className="px-5 pt-4 pb-28 min-h-screen bg-bone"
      style={VAULT_SURFACE_STYLE}
      data-testid="vault-screen"
    >
      <div className="flex items-center justify-between mb-5">
        <BrandMark />
        <AccountButton openPaywall={() => openPaywall?.()} />
      </div>

      {/* Hero card */}
      <div className="bg-white rounded-2xl shadow-soft border border-navy/5 p-5 mb-3 relative overflow-hidden">
        <div className="absolute right-0 top-0 bottom-0 w-[42%] flex items-center justify-center pointer-events-none">
          <VaultIllustration className="w-full h-full" />
        </div>
        <div className="relative w-[58%]">
          <div className="flex items-center gap-2">
            <h1 className="font-jakarta font-extrabold text-navy text-2xl leading-tight">
              Travel Vault
            </h1>
            <ShieldCheck size={22} weight="fill" color="#2E7D5A" />
          </div>
          <p className="text-navy-soft text-sm font-jakarta font-medium leading-snug mt-1">
            Store travel essentials, memberships & trusted-traveler info in one
            secure place.
          </p>
        </div>
      </div>

      <SyncStatusPill
        isSignedIn={isSignedIn}
        status={syncStatus}
        onSignIn={() => openPaywall?.()}
      />

      <div className="space-y-3">
        {CATEGORIES.map((cat) => {
          const count = counts[cat.id] || 0;
          return (
            <button
              key={cat.id}
              type="button"
              data-testid={`vault-category-${cat.id}`}
              onClick={() => setActiveCat(cat.id)}
              className="w-full bg-white rounded-2xl shadow-soft border border-navy/5 p-4 flex items-center gap-4 text-left hover:shadow-float transition-shadow"
            >
              {cat.buttonSrc ? (
                // 3D neon button artwork — already has its own steel
                // frame + glow, so we skip the pastel `cat.soft` chip
                // wrapper and let the asset speak for itself. Squared
                // aspect so the frame stays unsquished across categories.
                <img
                  src={cat.buttonSrc}
                  alt=""
                  aria-hidden
                  className="shrink-0 w-14 h-14 object-contain"
                  draggable={false}
                />
              ) : (
                <div
                  className="shrink-0 w-12 h-12 rounded-2xl flex items-center justify-center"
                  style={{ backgroundColor: cat.soft }}
                >
                  <CatIcon cat={cat} size={24} />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="font-jakarta font-extrabold text-navy text-base leading-tight">
                  {cat.label}
                </div>
                <div className="text-navy-soft text-sm font-jakarta font-medium mt-0.5">
                  {cat.live
                    ? count === 0
                      ? cat.sub
                      : `${count} saved`
                    : cat.sub}
                </div>
              </div>
              <CaretRight size={18} weight="bold" color="#4A5D78" />
            </button>
          );
        })}
      </div>

      <p className="text-center text-navy-soft text-xs font-jakarta font-medium mt-5">
        {isSignedIn
          ? "Your Vault is synced to your TipLingo account across devices."
          : "Everything in your Vault stays on this device. Sign in to sync across devices."}
      </p>
    </div>
  );
}
